import { Popover as MuiPopover, PopoverProps } from '@mui/material';
import React, {
  Dispatch,
  FC,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  SetStateAction,
  useId,
  useState,
} from 'react';

import { getPaperStyles } from './styled';

type BasePopoverProps = Omit<PopoverProps, 'anchorEl' | 'open'>;

export type ExternalPopoverProps = {
  anchorEl: HTMLButtonElement | null;
  onStateChange: Dispatch<SetStateAction<HTMLButtonElement | null>>;
};

/**
 * This way we ensure that both external props are passed
 */
type Props = BasePopoverProps & {
  externalHandlerProps?: ExternalPopoverProps;
  shouldHaveOffset?: boolean;
  trigger: ReactElement;
};

/**
 * Popover component as wrapper around MUI Popover
 *
 * @param props                                    Props passed to the component
 * @param props.children                           The children to render inside the popover
 * @param props.externalHandlerProps               Props for managing the popover state from parent component (optional)
 * @param props.externalHandlerProps.anchorEl      Anchor element for popover from parent component
 * @param props.externalHandlerProps.onStateChange Callback when the popover state is changed from parent component
 * @param props.shouldHaveOffset                   Should have offset for the popover paper
 * @param props.trigger                            The element that triggers the popover
 *
 * @returns                                        Component itself
 *
 * @summary If externalHandlerProps are not provided, the popover will manage its own state
 */
const Popover: FC<Props> = ({
  children,
  externalHandlerProps,
  shouldHaveOffset = false,
  trigger,
  ...restProps
}) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const popoverId = useId();

  const shouldUseExternalHandling = externalHandlerProps !== undefined;

  const { anchor, requestSetState } = shouldUseExternalHandling
    ? {
        anchor: externalHandlerProps.anchorEl,
        requestSetState: externalHandlerProps.onStateChange,
      }
    : { anchor: anchorElement, requestSetState: setAnchorElement };

  /**
   * Toggle popover
   *
   * @param target Target of the triggered event
   */
  const toggleState = (target: HTMLElement) => {
    requestSetState(prev => (prev !== null ? null : target));
  };

  /**
   * Handle the click event on trigger element
   *
   * @param event Event that took place
   */
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    toggleState(event.currentTarget);
  };

  /**
   * Handle the close event on popover
   */
  const handleClose = () => {
    requestSetState(null);
  };

  /**
   * Handle the keyboard event on trigger element
   *
   * @param event Event that took place
   * @summary This is support for elements without role used for trigger
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const targetRole = event.currentTarget.getAttribute('role');

    if (targetRole !== null) {
      return;
    }

    if (event.key === 'Enter') {
      toggleState(event.currentTarget);
    }
  };

  const shouldShowContent = Boolean(anchor);

  return (
    <>
      {React.cloneElement(trigger, {
        'aria-controls': shouldShowContent ? popoverId : undefined,
        'aria-describedby': popoverId,
        'aria-expanded': shouldShowContent ? 'true' : undefined,
        'aria-haspopup': 'true',
        onClick: handleClick,
        onKeyDown: handleKeyDown,
      })}
      <MuiPopover
        anchorEl={anchor}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        id={popoverId}
        onClose={handleClose}
        open={shouldShowContent}
        slotProps={{
          paper: {
            elevation: 5,
            sx: getPaperStyles(shouldHaveOffset),
          },
        }}
        {...restProps}
      >
        {children}
      </MuiPopover>
    </>
  );
};

export default Popover;
